import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { VariantColor } from '@repo/toolkit/colors'
import { generateColorForId } from '@repo/toolkit/generate-map-store'
import { StringFormatter } from '@repo/toolkit/string'
import { SVGFC } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import { InformationIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import React from 'react'

import {
  FriendPlaceEnum,
  friendPlaceEnumMapping
} from '../../../lib/types/enums/friend/friend-place.enum'
import { tripStepTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { JourneyLight } from '../../../lib/types/models/product/journey'
import { TripDate } from '../../../lib/types/models/trip-booking/trip-date'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

type LabeledItemProps = {
  label: string
  className?: string
  children?: React.ReactNode
}

/**
 * A component to display a label and a children. Used by both the primary and secondary trip dates update pages
 * @param label - The label to display
 * @param children - The children to display
 * @returns A component to display a label and a children
 */
export function LabeledItem({ label, children, className }: LabeledItemProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
        {label}
      </Typography>

      {children}
    </div>
  )
}

/**
 * Badge to display the directionality of a trip date
 * @param directionality - The directionality of the trip date
 * @returns A badge to display the directionality of a trip date
 */
export function TripDateDirectionalityBadge({
  directionality
}: {
  directionality: TripStepTypeEnum
}) {
  const { label, variant } = tripStepTypeMapping[directionality]
  return <Badge variant={variant} text={label} />
}

type NumberOfPlacesBadgeProps = {
  studentsPlaces: number
  staffPlaces?: number
  className?: string
}

/**
 * Badge to display the number of places for a trip date, one for the students and one for the staff
 * @param numberOfStudentsPlaces - The number of students places
 * @param numberOfStaffPlaces - The number of staff places
 * @returns 2 Badges to display the number of places for a trip date, one for the students and one for the staff
 */
export function NumberOfPlacesBadge({
  studentsPlaces,
  staffPlaces,
  className
}: NumberOfPlacesBadgeProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Badge text={`${studentsPlaces} jeunes`} variant={'fushia'} />
      <Badge text={`${staffPlaces} encadrants`} variant={'blue'} />
    </div>
  )
}

type PaxMessageBadgeProps = {
  numberOfStudents?: number
  numberOfStaff?: number
  color?: TailwindColor
  icon?: SVGFC
  className?: string
  suffix: string
}

/**
 * Badge to display a pax message of a trip date (e.g. places to dispatch, places to allocate)
 * @param numberOfStudents - The number of students places
 * @param numberOfStaff - The number of staff places
 * @param suffix - The action suffix appended after the counts (e.g. 'à dispatcher', 'à répartir')
 * @returns A badge with an icon, and the number of students or staffs or both with the suffix
 */
export function PaxMessageBadge({
  className,
  numberOfStaff,
  numberOfStudents,
  icon: Icon = InformationIcon,
  color = tailwindColors.secondary.DEFAULT,
  suffix
}: PaxMessageBadgeProps) {
  return (
    <div
      className={cn(
        'flex flex-row gap-1 bg-secondary-light px-2 py-1 items-center rounded-sm',
        className
      )}
    >
      <Icon className={'w-5 h-5'} color={color} />

      <div className={'flex flex-col gap-1'}>
        {numberOfStudents && numberOfStudents > 0 ? (
          <Typography
            variant={'label-semibold'}
            color={color}
          >{`${numberOfStudents} jeunes ${suffix}`}</Typography>
        ) : null}

        {numberOfStaff && numberOfStaff > 0 ? (
          <Typography
            variant={'label-semibold'}
            color={color}
          >{`${numberOfStaff} encadrants ${suffix}`}</Typography>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Badge to display the ecart of a trip date
 * @param tripDate - The trip date to display the ecart of
 * @returns A badge to display the ecart of a trip date
 */
export function TripDateEcartBadge({ tripDate }: { tripDate: TripDate }) {
  let studentsVariant: VariantColor = 'grey'
  let staffVariant: VariantColor = 'grey'
  const { numberOfRemainingStudentPlaces, numberOfRemainingStaffPlaces } = tripDate

  // 1. If the number of people reserved is greater than the number of people requested, the difference appears in gray.
  if (numberOfRemainingStudentPlaces > 0) studentsVariant = 'grey'
  if (numberOfRemainingStaffPlaces > 0) staffVariant = 'grey'

  // 2. If the number of people reserved is less than the number of people requested, the difference appears in red.
  if (numberOfRemainingStudentPlaces < 0) studentsVariant = 'danger'
  if (numberOfRemainingStaffPlaces < 0) staffVariant = 'danger'

  // 3. If the number of people reserved is equal to the number of people requested, the difference appears in green.
  if (numberOfRemainingStudentPlaces === 0) studentsVariant = 'success'
  if (numberOfRemainingStaffPlaces === 0) staffVariant = 'success'

  return (
    <div className={'flex flex-col gap-2 py-2'}>
      <Badge
        variant={studentsVariant}
        text={`${numberOfRemainingStudentPlaces > 0 ? '+' : ''}${numberOfRemainingStudentPlaces}`}
      />
      <Badge
        variant={staffVariant}
        text={`${numberOfRemainingStaffPlaces > 0 ? '+' : ''}${numberOfRemainingStaffPlaces}`}
      />
    </div>
  )
}

type FriendBadgeProps = {
  firstName: string
  lastName: string
  type?: FriendPlaceEnum
  variant?: VariantColor
}

/**
 * Badge to display the details of a friend
 * @param firstName - The first name of the friend
 * @param lastName - The last name of the friend
 * @param type - The type of the friend
 * @param variant - The variant of the badge
 * @returns A badge to display the details of a friend
 */
export function FriendBadge({ firstName, lastName, type, variant = 'blue' }: FriendBadgeProps) {
  const data: string[] = []

  data.push(lastName.toUpperCase())
  data.push(StringFormatter.capitalize(firstName))

  if (type) {
    data.push('-')
    data.push(friendPlaceEnumMapping[type].title)
  }

  return <Badge variant={variant} text={data.join(' ')} />
}

type JourneyBadgeProps = {
  journey: JourneyLight
  className?: string
}

/**
 * Badge to display the details of a journey
 * @param journey - The journey to display the details of
 * @param variant - The variant of the badge
 * @param className - The className of the badge
 * @returns A badge to display the details of a journey
 */
export function JourneyBadge({ journey, className }: JourneyBadgeProps) {
  const { reference, mainOption, date } = journey

  const color = generateColorForId(journey.id)

  return (
    <Badge
      color={`${color}25`}
      textColor={color}
      className={className}
      text={`${reference} - ${date.product.name} - ${getTranslatedName(mainOption)}`}
    />
  )
}
